import {
  Button,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import { trimOnBlur } from "../../../utils/trimOnBlur";

export type InputMatches = {
  regexp: RegExp;
  failMessage: string;
};

interface Values {
  text: string;
}

interface Props {
  initText?: string;
  textLabel?: string;
  isNumber?: boolean;
  inputComponent?: any;
  noneTextOption?: string;
  errorText?: string;
  prefix?: string;
  suffix?: string;
  inputMatches?: InputMatches;
  required?: boolean;
  charLimit?: number;
  multiline?: boolean;
  rows?: number;
  onComplete: (text: string) => Promise<boolean>;
  onBack?: () => void;
  centerButtons?: boolean;
}

export const SingleTextForm = ({
  initText,
  textLabel,
  isNumber,
  inputComponent,
  noneTextOption,
  errorText,
  prefix,
  suffix,
  multiline,
  inputMatches,
  required,
  charLimit,
  rows,
  onComplete,
  onBack,
  centerButtons,
}: Props): JSX.Element => {
  // https://formik.org/docs/api/formik

  // Required by default.
  if (required == undefined) {
    required = true;
  }

  const [charCount, setCharCount] = React.useState(
    initText ? initText.length : 0
  );

  const formikInit = {
    values: {
      text: initText || "",
    },
    schema: Yup.object().shape({
      text: inputMatches
        ? Yup.string().matches(inputMatches.regexp, inputMatches.failMessage)
        : required
        ? Yup.string().required(errorText || `${textLabel} is required`)
        : Yup.string(),
    }),
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      if (charLimit && values.text.length > charLimit) {
        throw Error(`"${textLabel}" must be ${charLimit} characters or less`);
      }
      await onComplete(values.text);
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      setSubmitting(false);
    }
  };

  const inputProps =
    prefix || suffix
      ? {
          startAdornment: prefix ? (
            <InputAdornment position="start">{prefix}</InputAdornment>
          ) : undefined,
          endAdornment: prefix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : undefined,
          inputComponent: inputComponent ? (inputComponent as any) : undefined,
        }
      : {
          inputComponent: inputComponent ? (inputComponent as any) : undefined,
        };

  return (
    <Formik
      initialValues={formikInit.values}
      validationSchema={formikInit.schema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        status,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={1}
            sx={{
              my: 1,
            }}
          >
            <Grid item xs={12}>
              <TextField
                multiline={multiline}
                InputProps={inputProps}
                error={
                  Boolean(touched.text && errors.text) ||
                  (charLimit && charCount > charLimit ? true : false)
                }
                fullWidth
                rows={multiline && rows ? rows : 1}
                helperText={
                  charLimit ? `${charLimit - charCount} characters left` : ""
                }
                label={(textLabel && textLabel.length < 30) ? textLabel : "Enter response"}
                margin="normal"
                name="text"
                onBlur={(evt: React.FocusEvent<any>) => {
                  trimOnBlur(evt, "text", setFieldValue, handleBlur);
                }}
                onChange={(event) => {
                  setCharCount(event.target.value.length);
                  handleChange(event);
                }}
                type={isNumber ? "number" : "text"}
                value={values.text}
                variant="outlined"
              />
              <Typography variant="body1" color="error">
                {touched.text && errors.text}
              </Typography>
            </Grid>

            {noneTextOption && (
              <Grid item xs={12}>
                <Button
                  variant="text"
                  color="secondary"
                  onClick={async () => {
                    await onComplete("");
                  }}
                  sx={{
                    minWidth: 0,
                  }}
                >
                  {noneTextOption}
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <ScreenFlowFormButtons
                isSubmitting={isSubmitting}
                status={status}
                onBack={onBack}
                centered={centerButtons}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};
